import React, { useState, useRef, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Icon } from '@iconify/react';
import { Switch } from '../switch/switch';
import styles from './card-form.module.css';
import iconoTarjeta from './assets/icono-tarjeta.png';
import iconoUsuario from './assets/icono-usuario.png';
import iconoCalendario from './assets/icono-calendario.png';
import iconoCvv from './assets/icono-cvv.png';
import iconoCandado from './assets/icono-candado.png';
import iconoDeposito from '../../assets/icono-boton-depositar.png';
import iconoMonto from '../../assets/icono-saldo.png';
import usePaymentStore from '../../store/use-payment-store';
import useUserContext from '../../../../../hooks/useUserContext';
import numeral from 'numeral';
import WarningCard from './warning-card';


export const CardForm = ({
  onSubmit,
  showSaveCard = true,
  submitButtonText = 'Depositar',
  isSubmitting = false,
  disabled = false,
  submitButtonClassName = '',
  logoBrand = null,
  onNavigateToOtherCard,
}) => {
  const data = useUserContext();
  const maxDeposit = data?.userLoyalty?.lastLevel?.deposit_limit / 100 ?? 0;
  const minAmount = 50;
  const maxAmount = maxDeposit;
  const { amount, setAmount } = usePaymentStore();
  const [saveCard, setSaveCard] = useState(false);
  const [showAmexOverlay, setShowAmexOverlay] = useState(false);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const expirationInputRef = useRef(null);
  const holderNameInputRef = useRef(null);
  const cvvInputRef = useRef(null);
  const [cardNumber, setCardNumber] = useState('');
  const cardNumberInputRef = useRef(null);
  const maskRef = useRef(null);

  useEffect(() => {
    if (cardNumberInputRef.current) {

      const blocks = {
        0: {
          mask: IMask.MaskedRange,
          from: 0,
          to: 9,
        },
        d: {
          mask: IMask.MaskedRange,
          from: 0,
          to: 9,
          displayChar: '*',
          overwrite: 'shift',
        },
      };

      maskRef.current = IMask(cardNumberInputRef.current, {
        mask: [
          {
            mask: '0000 00dd dddd 0000',
            startsWith: '4',
            lazy: true,
            brand: 'Visa',
            blocks: blocks,
          },
          {
            mask: '0000 00dddd d0000',
            startsWith: '3',
            lazy: true,
            brand: 'American Express',
            blocks: blocks,
          },
          {
            mask: '0000 00dd dddd 0000',
            startsWith: '5',
            lazy: true,
            brand: 'MasterCard',
            blocks: blocks,
          },
          {
            mask: '0000 00dd dddd 0000',
            startsWith: '',
            lazy: true,
            blocks: blocks,
          }
        ],
        dispatch: (appended, dynamicMasked) => {
          const number = (dynamicMasked.value + appended).replace(/\D/g, '');

          return dynamicMasked.compiledMasks.find(m => number.indexOf(m.startsWith) === 0);
        }
      });
    }

    return () => {
      if (maskRef.current) {
        maskRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentUnmasked = maskRef.current?.unmaskedValue ?? '';
      if (currentUnmasked !== cardNumber) {
        setCardNumber(currentUnmasked);
        setFormData(prev => ({
          ...prev,
          cardNumber: currentUnmasked,
        }));
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [maskRef.current?.unmaskedValue]);


  // Estados del formulario
  const [formData, setFormData] = useState({
    cardNumber: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    displaySaveCard: false,
  });

  // Estados de errores
  const [errors, setErrors] = useState({
    amount: '',
    cardNumber: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
  });

  // Handler para el cambio del monto usando CurrencyInput
  const handleAmountChange = (value, name, values) => {
    // Si value es undefined o null, significa que el campo está vacío
    if (value === undefined || value === null || value === '') {
      setAmount(null);
      setErrors(prev => ({
        ...prev,
        amount: '',
      }));
      return;
    }

    // Obtener el valor numérico del objeto values
    const numValue = values && values.float !== undefined ? values.float : null;

    // Si no hay valor numérico válido, limpiar
    if (numValue === null || numValue === undefined || isNaN(numValue)) {
      setAmount(null);
      setErrors(prev => ({
        ...prev,
        amount: '',
      }));
      return;
    }

    // Actualizar el store con el valor numérico
    setAmount(numValue);

    // Validación de monto mínimo y máximo
    let error = '';
    if (numValue <= 0) {
      error = `El monto debe ser mayor a ${numeral(minAmount).format('$0,000')}`;
    } else if (numValue < minAmount) {
      error = `El monto mínimo es de ${numeral(minAmount).format('$0,000')}`;
    } else if (numValue > maxAmount && maxAmount > 0) {
      error = `El monto máximo es de ${numeral(maxAmount).format('$0,000')}`;
    }

    setErrors(prev => ({
      ...prev,
      amount: error,
    }));
  };

  // Validaciones
  const validateAmount = (value) => {
    // Convertir a número si es necesario
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

    // Validar que sea un número válido
    if (!value || isNaN(numValue) || numValue <= 0) {
      return `El monto debe ser mayor a ${numeral(minAmount).format('$0,000')}`;
    }
    if (numValue < minAmount) {
      return `El monto mínimo es de ${numeral(minAmount).format('$0,000')}`;
    }
    if (numValue > maxAmount && maxAmount > 0) {
      return `El monto máximo es de ${numeral(maxAmount).format('$0,000')}`;
    }
    return '';
  };

  const validateCardNumber = (value) => {
    // Usar el valor pasado o el del mask (número de tarjeta no está en formData)
    const rawValue = value !== undefined && value !== null
      ? value
      : (maskRef.current?.unmaskedValue ?? '');
    const cleaned = (rawValue ?? '').replace(/\D/g, '');

    if (!cleaned) {
      return 'El número de tarjeta es requerido';
    }
    if (!/^\d+$/.test(cleaned)) {
      return 'El número de tarjeta solo debe contener dígitos';
    }
    if (cleaned.length < 14 || cleaned.length > 16) {
      return 'El número de tarjeta debe tener entre 14 y 16 dígitos';
    }
    return '';
  };

  const validateHolderName = (value) => {
    if (!value.trim()) {
      return 'El nombre del titular es requerido';
    }
    if (value.trim().length < 2) {
      return 'El nombre debe tener al menos 2 caracteres';
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
      return 'El nombre solo debe contener letras y espacios';
    }
    return '';
  };

  const validateExpiryMonth = (value, year = null) => {
    if (!value) {
      return 'El mes es requerido';
    }
    const month = parseInt(value, 10);
    if (month < 1 || month > 12) {
      return 'El mes debe ser entre 1 y 12';
    }

    // Si tenemos el año, validar que la fecha no haya expirado
    if (year !== null && year !== '') {
      const expiryYear = parseInt(year, 10);
      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;

      // Si el año es el actual y el mes ya pasó, la tarjeta ha expirado
      if (expiryYear === currentYear && month < currentMonth) {
        return 'La tarjeta ha expirado';
      }
    }

    return '';
  };

  const validateExpiryYear = (value, month = null) => {
    if (!value) {
      return 'El año es requerido';
    }
    const year = parseInt(value, 10);
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (year < currentYear) {
      return 'El año no puede ser anterior al actual';
    }
    if (year > currentYear + 20) {
      return 'El año no puede ser más de 20 años en el futuro';
    }

    // Si tenemos el mes y el año es el actual, validar que el mes no haya pasado
    if (month !== null && month !== '' && year === currentYear) {
      const expiryMonth = parseInt(month, 10);
      if (expiryMonth < currentMonth) {
        return 'La tarjeta ha expirado';
      }
    }

    return '';
  };

  const validateCvv = (value) => {
    if (!value) {
      return 'El CVV es requerido';
    }
    if (!/^\d+$/.test(value)) {
      return 'El CVV solo debe contener dígitos';
    }
    if (value.length < 3 || value.length > 4) {
      return 'El CVV debe tener 3 o 4 dígitos';
    }
    return '';
  };

  const validateExpiryDate = (monthValue, yearValue) => {
    const month = parseInt(monthValue, 10);
    const year = parseInt(yearValue, 10);
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (year === currentYear && month < currentMonth) {
      setErrors(prev => ({
        ...prev,
        expirationMonth: 'La tarjeta ha expirado',
        expirationYear: 'La tarjeta ha expirado',
      }));
      return false;
    }
    return true;
  };

  // Handlers
  const handleInputChange = (field, value) => {
    let processedValue = value;

    // El número de tarjeta ahora se maneja con MaskPW, no necesitamos formatearlo aquí
    if (field === 'cardNumber') {
      // MaskPW ya maneja el formateo y enmascarado
      // Solo actualizamos el estado si es necesario para otras validaciones
      return;
    }

    // Limitar longitud según el campo
    if (field === 'cvv') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (field === 'expirationMonth') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
    } else if (field === 'expirationYear') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }));

    // Validación en tiempo real
    let error = '';
    switch (field) {
      case 'cardNumber':
        error = validateCardNumber(processedValue);
        break;
      case 'holderName':
        error = validateHolderName(processedValue);
        break;
      case 'expirationMonth':
        error = validateExpiryMonth(processedValue, formData.expirationYear);
        // También validar el año si existe, para asegurar consistencia
        if (!error && formData.expirationYear) {
          const yearError = validateExpiryYear(formData.expirationYear, processedValue);
          if (yearError) {
            setErrors(prev => ({
              ...prev,
              expirationYear: yearError,
            }));
          }
        }
        break;
      case 'expirationYear':
        error = validateExpiryYear(processedValue, formData.expirationMonth);
        // También validar el mes si existe, para asegurar consistencia
        if (!error && formData.expirationMonth) {
          const monthError = validateExpiryMonth(formData.expirationMonth, processedValue);
          if (monthError) {
            setErrors(prev => ({
              ...prev,
              expirationMonth: monthError,
            }));
          }
        }
        break;
      case 'cvv':
        error = validateCvv(processedValue);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = () => {
    // Número de tarjeta: usar valor del mask
    const currentCardNumber = maskRef.current?.unmaskedValue ?? cardNumber ?? formData.cardNumber ?? '';

    // Sincronizar posibles valores autocompletados desde los inputs nativos
    const currentHolderName =
      formData.holderName ||
      holderNameInputRef.current?.value ||
      '';

    let currentExpirationMonth = formData.expirationMonth;
    let currentExpirationYear = formData.expirationYear;

    if (!currentExpirationMonth && !currentExpirationYear && expirationInputRef.current?.value) {
      const cleaned = expirationInputRef.current.value.replace(/\D/g, '');
      currentExpirationMonth = cleaned.slice(0, 2);
      currentExpirationYear = cleaned.slice(2, 4);
    }

    const currentCvv =
      formData.cvv ||
      cvvInputRef.current?.value ||
      '';

    const newErrors = {
      amount: validateAmount(amount),
      cardNumber: validateCardNumber(currentCardNumber),
      holderName: validateHolderName(currentHolderName),
      expirationMonth: validateExpiryMonth(currentExpirationMonth, currentExpirationYear),
      expirationYear: validateExpiryYear(currentExpirationYear, currentExpirationMonth),
      cvv: validateCvv(currentCvv),
    };

    // Actualizar el estado con los valores sincronizados (por si vinieron de autocompletar)
    setFormData(prev => ({
      ...prev,
      expirationMonth: currentExpirationMonth,
      expirationYear: currentExpirationYear,
      cvv: currentCvv,
    }));

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(error => error !== '');
    if (!hasErrors) {
      return validateExpiryDate(currentExpirationMonth, currentExpirationYear);
    }

    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar datos para enviar
    // formData.cardNumber ya viene sin espacios ni máscara desde MaskPW
    const cardData = {
      cardNumber: cardNumber, // Asegurar que solo sean dígitos
      holderName: formData.holderName.trim(),
      expirationMonth: formData.expirationMonth.padStart(2, '0'),
      expirationYear: `${formData.expirationYear}`,
      cvv: formData.cvv,
      saveCard,
    };

    // Llamar al callback onSubmit con los datos
    if (onSubmit) {
      onSubmit(cardData);
    }
  };

  // Función auxiliar para calcular el display de expiración
  const calculateExpirationDisplay = (month, year) => {
    if (!month && !year) return '';

    if (month && !year) {
      if (month.length === 1) {
        return month;
      }
      const formatted = month.padStart(2, '0');
      return formatted === '00' ? '' : formatted;
    }

    if (month === '00') {
      return '';
    }
    const formattedMonth = month.length === 1 ? month : month.padStart(2, '0');
    if (formattedMonth === '00') {
      return '';
    }
    return `${formattedMonth} / ${year}`;
  };

  const handleExpirationChange = (e) => {
    const input = e.target;
    const value = input.value || '';

    // Solo tomamos dígitos para mes/año
    const cleaned = value.replace(/\D/g, '').slice(0, 4);
    const month = cleaned.slice(0, 2);
    const year = cleaned.slice(2, 4);

    setFormData(prev => ({
      ...prev,
      expirationMonth: month,
      expirationYear: year,
    }));

    const monthError = month ? validateExpiryMonth(month, year) : '';
    const yearError = year ? validateExpiryYear(year, month) : '';

    setErrors(prev => ({
      ...prev,
      expirationMonth: monthError,
      expirationYear: yearError,
    }));

    if (month && year && !monthError && !yearError) {
      validateExpiryDate(month, year);
    }

    // Después de actualizar el estado, mover el cursor al final del valor formateado
    setTimeout(() => {
      if (expirationInputRef.current) {
        const display = calculateExpirationDisplay(month, year);
        const pos = display.length;
        expirationInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const getExpirationDisplay = () => {
    const month = formData.expirationMonth || '';
    const year = formData.expirationYear || '';

    // Si no hay mes ni año, retornar vacío
    if (!month && !year) return '';

    // Si solo hay mes, formatearlo apropiadamente
    if (month && !year) {
      // Si el mes tiene 1 dígito, mostrarlo tal cual (permite "0", "1", etc.)
      if (month.length === 1) {
        return month;
      }
      // Si el mes tiene 2 dígitos, formatearlo con padStart
      // Pero evitar "00" - si es "00", mostrar vacío
      const formatted = month.padStart(2, '0');
      return formatted === '00' ? '' : formatted;
    }

    // Si hay mes y año, formatear ambos
    // Evitar "00" en el mes cuando hay año también
    if (month === '00') {
      return '';
    }
    const formattedMonth = month.length === 1 ? month : month.padStart(2, '0');
    // Si después de padStart resulta en "00", no mostrar nada
    if (formattedMonth === '00') {
      return '';
    }
    return `${formattedMonth} / ${year}`;
  };

  const handleNavigateToOtherCard = () => {
    if (onNavigateToOtherCard) {
      onNavigateToOtherCard();
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} style={{ position: 'relative' }}>
      {showAmexOverlay && (
        <div className={styles.amexOverlay}>
          <div className={styles.amexOverlayContent}>
            <p className={styles.amexMessage}>
              Al parecer tu tarjeta es Amex, si es así presiona este botón para procesar el abono con tu tarjeta
            </p>
            <button
              type="button"
              className="payment-button"
              // className={styles.amexButton}
              onClick={handleNavigateToOtherCard}
            >
              Procesar con Amex
            </button>
          </div>
        </div>
      )}
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="amount">
          Monto a depositar
        </label>
        <div className={`${styles.inputWrapper} ${errors.amount ? styles.inputWrapperError : ''}`}>
          <img src={iconoMonto} alt="Monto" className={styles.icon} />
          <CurrencyInput
            id="amount"
            name="amount"
            className={styles.input}
            value={amount && amount !== '' && amount !== 0 && amount !== null ? amount : undefined}
            onValueChange={handleAmountChange}
            onFocus={() => setIsAmountFocused(true)}
            onBlur={() => setIsAmountFocused(false)}
            decimalsLimit={2}
            {...(!isAmountFocused && { fixedDecimalLength: 2 })}
            decimalSeparator="."
            groupSeparator=","
            prefix=""
            placeholder="0,000.00"
            allowDecimals={true}
            allowNegativeValue={false}
          />
        </div>
        {errors.amount && (
          <span className={styles.errorMessage}>{errors.amount}</span>
        )}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="holderName">
          Nombre del titular
        </label>
        <div className={`${styles.inputWrapper} ${errors.holderName ? styles.inputWrapperError : ''}`}>
          <img src={iconoUsuario} alt="Usuario" className={styles.icon} />
          <input
            autoFocus
            id="holderName"
            type="text"
            className={styles.input}
            placeholder="Tu nombre completo"
            value={formData.holderName}
            onChange={(e) => handleInputChange('holderName', e.target.value)}
            maxLength={50}
            ref={holderNameInputRef}
          />
        </div>
        {errors.holderName && (
          <span className={styles.errorMessage}>{errors.holderName}</span>
        )}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="cardNumber">
          Número de tarjeta
        </label>
        <div className={`${styles.inputWrapper} ${errors.cardNumber ? styles.inputWrapperError : ''}`}>
          <img src={iconoTarjeta} alt="Tarjeta" className={styles.icon} />
          <input
            type="text"
            className={`${styles.input}`}
            placeholder="1234 1234 1234 1234"
            inputMode="numeric"
            ref={cardNumberInputRef}
          />
        </div>
        {errors.cardNumber && (
          <span className={styles.errorMessage}>{errors.cardNumber}</span>
        )}
      </div>

      <div className={styles.row}>
        <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
          <label className={styles.label} htmlFor="expiration">
            Vencimiento
          </label>
          <div className={`${styles.inputWrapper} ${errors.expirationMonth || errors.expirationYear ? styles.inputWrapperError : ''}`}>
            <img src={iconoCalendario} alt="Calendario" className={styles.icon} />
            <input
              ref={expirationInputRef}
              id="expiration"
              type="text"
              className={styles.input}
              placeholder="MM / AA"
              value={getExpirationDisplay()}
              onChange={handleExpirationChange}
              // onKeyDown={handleExpirationKeyDown}
              maxLength={7}
              inputMode="numeric"
            />
          </div>
          {(errors.expirationMonth || errors.expirationYear) && (
            <span className={styles.errorMessage}>
              {errors.expirationMonth || errors.expirationYear}
            </span>
          )}
        </div>

        <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
          <label className={styles.label} htmlFor="cvv">
            CVV
          </label>
          <div className={`${styles.inputWrapper} ${errors.cvv ? styles.inputWrapperError : ''}`}>
            <img src={iconoCvv} alt="CVV" className={styles.icon} />
            <input
              id="cvv"
              type="password"
              className={styles.input}
              placeholder="000"
              value={formData.cvv}
              onChange={(e) => handleInputChange('cvv', e.target.value)}
              maxLength={4}
              inputMode="numeric"
              ref={cvvInputRef}
            />
          </div>
          {errors.cvv && (
            <span className={styles.errorMessage}>{errors.cvv}</span>
          )}
        </div>
      </div>

      <div className={styles.securityMessage}>
        <img src={iconoCandado} alt="Candado" className={styles.icon} />
        <span className={styles.securityText}>
          Tus datos estan protegidos con encriptación de nivel bancario
        </span>
      </div>

      {showSaveCard && (
        <div className={styles.switchContainer}>
          <Switch isSelected={saveCard} onChange={setSaveCard} label="Guardar tarjeta" />
        </div>
      )}

      {logoBrand && (
        <img src={logoBrand} alt="Logo de la marca" className={styles.logoBrand} />
      )}

      <button
        type="submit"
        className={submitButtonClassName || 'payment-button'}
        disabled={isSubmitting || disabled}
      >
        {isSubmitting ? (
          <>
            <Icon icon="eos-icons:loading" className={styles.spinner} />
            Procesando...
          </>
        ) : (
          <>
            {amount && amount !== '' && amount > 0
              ? `${submitButtonText} ${numeral(amount).format('$0,0')}`
              : submitButtonText}
            <img src={iconoDeposito} alt="Deposito" className={styles.icon} />
          </>
        )}
      </button>
      <WarningCard />
    </form>
  );
};

